import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../libs/cartLib';
import { renderPrice } from '../../libs/cartLib';

export default function PromoCodeInput({ cart, onPromoCodeApplied, onPromoCodeRemoved }) {
    const [promoCode, setPromoCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const { addPromoCode, removePromoCode } = useCart();

    // Check if a promo code is already applied
    const hasAppliedPromoCode = cart?.promo_codes && cart.promo_codes.length > 0;

    const handleApplyPromoCode = async () => {
        if (!promoCode.trim()) return;
        
        setIsLoading(true);
        setError('');
        setSuccess('');
        
        try {
            const updatedCart = await addPromoCode(promoCode.trim());
            setSuccess('¡Código promocional aplicado exitosamente!');
            setPromoCode('');
            if (onPromoCodeApplied) {
                onPromoCodeApplied(updatedCart);
            }
        } catch (err) {
            console.error('Error applying promo code:', err);
            let errorMessage = 'Error al aplicar el código promocional';
            
            if (err.response && err.response.data) {
                if (err.response.data.error === 'PROMO_CODE_NOT_FOUND') {
                    errorMessage = 'Código promocional no encontrado';
                } else if (err.response.data.error === 'PROMO_CODE_INACTIVE') {
                    errorMessage = 'Código promocional inactivo';
                } else if (err.response.data.error === 'PROMO_CODE_ALREADY_APPLIED') {
                    errorMessage = 'Código promocional ya aplicado';
                } else if (err.response.data.error === 'PROMO_CODE_USAGE_LIMIT_EXCEEDED') {
                    errorMessage = 'Límite de uso del código promocional excedido';
                } else if (err.response.data.error === 'PROMO_CODE_EXPIRED') {
                    errorMessage = 'Código promocional expirado';
                } else if (err.response.data.error === 'PROMO_CODE_NOT_APPLICABLE') {
                    errorMessage = 'Código promocional no aplicable a este carrito';
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            }
            
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemovePromoCode = async (code) => {
        setIsLoading(true);
        setError('');
        setSuccess('');
        try {
            const updatedCart = await removePromoCode(code);
            if (onPromoCodeRemoved) {
                onPromoCodeRemoved(updatedCart);
            }
        } catch (err) {
            console.error('Error removing promo code:', err);
            setError('Error al remover el código promocional');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleApplyPromoCode();
        }
    };

    return (
        <div className="promo-code-section">            
            {!hasAppliedPromoCode && (
                <>
                    <label htmlFor="promoCode">¿Tienes un código promocional?</label>
                    <div className="promo-code-input-group">                    
                        <Form.Control
                            type="text"
                            placeholder="Ingresa tu código promocional"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            onKeyPress={handleKeyPress}
                            className="promo-code-input"
                            disabled={isLoading}
                        />
                        <Button
                            variant="outline-primary"
                            onClick={handleApplyPromoCode}
                            disabled={!promoCode.trim() || isLoading}
                            className="promo-code-apply-btn"
                        >
                            <FontAwesomeIcon icon={faTag} /> Aplicar
                        </Button>
                    </div>
                </>
            )}
            
            {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
            {success && <Alert variant="success" className="mt-2">{success}</Alert>}
            
            {cart?.promo_codes?.map((promoCode, index) => (
                <div key={index} className="applied-promo-code">
                    <span className="promo-code-text">
                        <FontAwesomeIcon icon={faTag} /> {promoCode.code}
                    </span>
                    <span className="promo-code-discount">
                        {promoCode.discount_type === 'PERCENT' 
                            ? `${promoCode.discount_value}%` 
                            : `-${renderPrice(cart, promoCode.discount_amount)}`
                        }
                    </span>
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleRemovePromoCode(promoCode.code)}
                        className="promo-code-remove-btn"
                        disabled={isLoading}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </Button>
                </div>
            ))}
        </div>
    );
}
